import { FC, useEffect, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { client, MarketCategory, MarketCategoryFilterOptions } from '../../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../../components/forms/Pagination';
import { Section } from '../../../../components/ui/Section';
import CategoriesList from './CategoriesList';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

type Props = {
  onCreateNew: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilterChange?: (_instance: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialSearchTerm?: string;
};

const CategoriesTab: FC<Props> = ({ onCreateNew, onFilterChange, initialInstance, initialSearchTerm }) => {
  const { t } = useTranslation('categories');
  const [list, setList] = useState<MarketCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const { instances } = useInstanceMarket();
  const pageSize = 20;

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const options: MarketCategoryFilterOptions = {
        companyId: selectedInstance || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getMarketCategories(options);
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance || selectedInstance === 'all')
    {
      fetchCategories();
    }
    else {
      setList([]);
      setTotalCount(0);
    }
  }, [currentPage, searchTerm, selectedInstance]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(event.target.value, searchInput);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setCurrentPage(0);
    onFilterChange?.('all', '');
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={selectedInstance}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value="all">
              {t('allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem
                key={instance.id}
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <CategoriesList
            list={list}
            isLoading={isLoading}
            totalCount={totalCount}
            instances={instances}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && !isLoading && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default CategoriesTab;
